import { Checkbox, Label, Select } from "@components";
import PricingRecord, { PricingSettings } from "@models/PricingRecord";
import React, { BaseSyntheticEvent, useState } from "react";
import { Col, FormGroup, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { PricingCardProps } from "./IPricingCardProps";
import { useCustomField } from "./useCustomField";
import { endOfMonth } from "date-fns";
import { MONTHS } from "@components/forms/datePickerUtils";
import { MonthString } from "@util/types";

export default function YearlyBillingSettings(props: PricingCardProps) {
	const { control } =
		useFormContext<{ pricingSettings: PricingSettings | null }>();
	const { index, pricingSettings } = props;

	const pricingRecord: PricingRecord = pricingSettings[index];

	const yearlyBilling = pricingRecord?.yearlyBilling;

	const { setFieldChecked, isChecked } = useCustomField<
		PricingRecord["yearlyBilling"]
	>(`pricingSettings.${index}.yearlyBilling`, {
		month: 1,
		resetUsageMonth: null,
	});

	const [selectedMonth, setSelectedMonth] = useState<number>(
		yearlyBilling?.month || 1
	);

	return (
		<>
			<Controller
				name={`pricingSettings.${index}.yearlyBilling`}
				control={control}
				defaultValue={yearlyBilling || null}
				render={({ field }) => (
					<Checkbox
						value={!!field.value}
						label="Jährliche Vorauszahlung"
						onChange={(e: BaseSyntheticEvent) =>
							setFieldChecked(!!e.target?.checked)
						}
					/>
				)}
			/>
			<br />
			{isChecked && (
				<div>
					<FormGroup as={Row}>
						<Label style={{ marginLeft: 24 }} column sm="3">
							Abrechnungsmonat
						</Label>
						<Col sm="5">
							<Controller
								control={control}
								name={`pricingSettings.${index}.yearlyBilling.month`}
								defaultValue={selectedMonth}
								render={({ field }) => (
									<Select
										{...field}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
											const value = Number.parseInt(e.target.value, 10);
											setSelectedMonth(value);
											field.onChange(value);
										}}
										value={field.value?.toString()}
									>
										{MONTHS.map((month, i) => (
											<option key={month} value={i + 1}>
												{month}
											</option>
										))}
									</Select>
								)}
							/>
						</Col>
					</FormGroup>
					<FormGroup as={Row}>
						<Label style={{ marginLeft: 24 }} column sm="3">
							Nutzung berechnen ab
						</Label>
						<Col sm="5">
							<Controller
								control={control}
								name={`pricingSettings.${index}.yearlyBilling.resetUsageMonth`}
								defaultValue={yearlyBilling?.resetUsageMonth || null}
								render={({ field }) => (
									<ResetMonthPicker
										{...field}
										yearlyBillingMonth={selectedMonth}
										startDate={pricingRecord.availableFrom}
									/>
								)}
							/>
						</Col>
					</FormGroup>
					<br />
				</div>
			)}
		</>
	);
}

function ResetMonthPicker(props: {
	startDate: Date | null;
	yearlyBillingMonth: number;
	value: MonthString | null;
	onChange: (selectedMonth: MonthString | null) => void;
}) {
	const yearlyBillingMonthPadded = String(props.yearlyBillingMonth).padStart(
		2,
		"0"
	);

	const fromMonth = new Date(`2025-${yearlyBillingMonthPadded}-01`);
	const toMonth = endOfMonth(props.startDate ?? new Date(0)); // No months to pick without start date

	const options: { year: number; month: number; monthString: MonthString }[] =
		[];
	for (let i = fromMonth; i <= toMonth; i.setFullYear(i.getFullYear() + 1)) {
		const year = i.getFullYear();
		const month = i.getMonth() + 1;
		const monthPadded = String(month).padStart(2, "0");
		const monthString = `${year}-${monthPadded}` as MonthString;
		options.push({ year, month, monthString });
	}

	return (
		<Select
			name="resetUsageMonth"
			onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
				props.onChange(
					e.target.value === "null" ? null : (e.target.value as MonthString)
				)
			}
			value={props.value === null ? "null" : props.value}
		>
			<option key={"null"} value={"null"}>
				unbegrenzt
			</option>
			{options.map(({ year, month, monthString }) => (
				<option key={year} value={monthString}>
					{MONTHS[month - 1]} {year}
				</option>
			))}
		</Select>
	);
}
